import type { FC } from "react";
import type { WebAuth } from "auth0-js";
import { Flex, Message, Text } from "@huub-web/ui";
import { useTranslation } from "react-i18next";

import { LoginForm, type LoginFormProps } from "../../LoginForm";
import type { BaseSlideProps } from "../../../types";
import { useAuthConfig } from "../../../hooks/useAuthConfig";
import { LegalLinks } from "./LegalLinks";

import { slideContainerStyle } from "../AuthScreens.css";

export type LoginSlideProps = BaseSlideProps &
  Pick<
    LoginFormProps,
    "onSubmit" | "onSwitchToRegister" | "onLoadingChange" | "onForgotPassword"
  > & {
    webAuth: WebAuth | null;
    error: string | null;
    /**
     * Informational message to display on the login form
     */
    infoMessage?: string | null;
    onProviderLogin: (provider: string) => Promise<void>;
  };

/**
 * LoginSlide component for user login.
 * Displays the login form with social login options.
 */
export const LoginSlide: FC<LoginSlideProps> = ({
  leading,
  trailing,
  webAuth,
  error,
  infoMessage,
  onSubmit,
  onSwitchToRegister,
  onForgotPassword,
  onLoadingChange,
  onProviderLogin,
}) => {
  const { t } = useTranslation();
  const { legalLinks } = useAuthConfig();

  return (
    <Flex className={slideContainerStyle} direction="column" gap="m">
      {leading}
      <Flex direction="column" gap="s">
        <Text as="h2" variant="displayM" bold align="center">
          {t(
            infoMessage
              ? "authScreens.login.titleReturning"
              : "authScreens.login.title",
          )}
        </Text>

        {infoMessage && <Message variant="warning" body={infoMessage} />}

        <Text as="p" align="center">
          {t("authScreens.login.subtitle")}
        </Text>
      </Flex>
      <LoginForm
        onLoadingChange={onLoadingChange}
        webAuth={webAuth}
        error={error}
        onSubmit={onSubmit}
        onSwitchToRegister={onSwitchToRegister}
        onForgotPassword={onForgotPassword}
        onGoogleLogin={() => onProviderLogin("google-oauth2")}
        onMicrosoftLogin={() => onProviderLogin("windowslive")}
      />
      {legalLinks && legalLinks.length > 0 && <LegalLinks links={legalLinks} />}
      {trailing}
    </Flex>
  );
};
